import {StaticRichText} from '@waka/compat/StaticRichText';
'use client';

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  $isElementNode,
  $getNodeByKey,
  $createRangeSelection,
  $setSelection,
  EditorState,
  LexicalEditor,
  LexicalNode,
  $isTextNode,
  TextNode,
  SerializedTextNode,
} from 'lexical';
import { $patchStyleText } from '@lexical/selection';

import { TextItemHoverPreviewContext } from '../../context-provider';
import { useTextSelection } from '../../contexts/text-selection-context';
import {
  isTextItem,
  WORD_BREAK_STYLE,
  TextItem,
  TextSegment,
} from './text-item-type';
import { overrideTextItemWithHoverPreview } from './override-text-item-with-hover-preview';
import { resolveFontFamilyCssStack } from '../../utils/text/font-family-css-stack';
import { loadFontFromTextItem } from '../../utils/text/load-font-from-text-item';
import { turnFontStyleIntoCss } from '../../utils/text/turn-font-style-into-css';
import { useLinkingEnabled, useWriteContext } from '../../utils/use-context';
import { changeItem } from '../../state/actions/change-item';
import { unmarkTextAsEditing } from '../../state/actions/text-item-editing';
import { deleteItems } from '../../state/actions/delete-items';
import { syncEditedTitleToStore } from '../../state/actions/caption-translation-actions';

// ============================================
// CSS 스타일 파싱 유틸리티
// ============================================

const parseStyleString = (style: string): Record<string, string> => {
  const result: Record<string, string> = {};
  style.split(';').forEach((part) => {
    const [key, value] = part.split(':').map((s) => s.trim());
    if (key && value) {
      result[key] = value;
    }
  });
  return result;
};

const toShadowCssColor = (color: string, opacity: number): string => {
  if (color === 'transparent' || opacity <= 0) {
    return 'transparent';
  }

  if (!color.startsWith('#')) {
    return color;
  }

  const hex = color.slice(1);
  if (hex.length !== 6 && hex.length !== 8) {
    return color;
  }

  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const baseAlpha = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
  const finalAlpha = Math.max(0, Math.min(1, baseAlpha * opacity));

  return `rgba(${red}, ${green}, ${blue}, ${finalAlpha})`;
};

const getShadowFilter = (item: TextItem): string | undefined => {
  if (item.shadow?.visible !== true || item.shadowVisible === false) {
    return undefined;
  }

  return `drop-shadow(${item.shadow.offsetX}px ${item.shadow.offsetY}px ${
    item.shadow.blur
  }px ${toShadowCssColor(item.shadow.color, item.shadow.opacity)})`;
};

// ============================================
// 데이터 변환 함수
// ============================================

/**
 * TextSegment 배열을 Lexical EditorState로 변환
 */
const segmentsToEditorState = (
  segments: TextSegment[],
  editor: LexicalEditor,
  item: TextItem,
) => {
  const root = $getRoot();
  root.clear();

  const paragraph = $createParagraphNode();

  if (segments.length === 0) {
    // 빈 segments면 item.text 사용
    const textNode = $createTextNode(item.text);
    paragraph.append(textNode);
  } else {
    segments.forEach((segment) => {
      const textNode = $createTextNode(segment.text);

      // 스타일 적용
      const styles: string[] = [];
      if (segment.color) {
        styles.push(`color: ${segment.color}`);
      }
      if (segment.strokeColor) {
        styles.push(`-webkit-text-stroke-color: ${segment.strokeColor}`);
      }
      if (segment.fontFamily) {
        styles.push(`font-family: ${segment.fontFamily}`);
      }
      if (segment.fontWeight) {
        styles.push(`font-weight: ${segment.fontWeight}`);
      }
      if (segment.fontVariant) {
        styles.push(`font-style: ${segment.fontVariant}`);
      }
      if (segment.fontSize) {
        styles.push(`font-size: ${segment.fontSize}px`);
      }
      if (segment.opacity !== undefined) {
        styles.push(`opacity: ${segment.opacity}`);
      }
      if (styles.length > 0) {
        textNode.setStyle(styles.join('; '));
      }

      paragraph.append(textNode);
    });
  }

  root.append(paragraph);
};

/**
 * Lexical EditorState를 TextSegment 배열로 변환
 */
const editorStateToSegments = (
  editorState: EditorState,
  item: TextItem,
): TextSegment[] => {
  const segments: TextSegment[] = [];

  editorState.read(() => {
    const root = $getRoot();
    const paragraphs = root.getChildren();

    paragraphs.forEach((paragraph: LexicalNode, pIndex: number) => {
      if (pIndex > 0) {
        // 이전 segment에 줄바꿈 추가
        const lastSegment = segments[segments.length - 1];
        if (lastSegment) {
          lastSegment.text += '\n';
        } else {
          segments.push({ text: '\n' });
        }
      }

      if ($isElementNode(paragraph)) {
        const children = paragraph.getChildren();
        children.forEach((child: LexicalNode) => {
          if ($isTextNode(child)) {
            const text = child.getTextContent();
            const style = child.getStyle();

            const segment: TextSegment = { text };

            // CSS 스타일 파싱
            if (style) {
              const styleMap = parseStyleString(style);
              if (styleMap.color) {
                segment.color = styleMap.color;
              }
              if (styleMap['-webkit-text-stroke-color']) {
                segment.strokeColor = styleMap['-webkit-text-stroke-color'];
              }
              if (styleMap['font-family']) {
                segment.fontFamily = styleMap['font-family'];
              }
              if (styleMap['font-weight']) {
                segment.fontWeight = styleMap['font-weight'];
              }
              if (styleMap['font-style']) {
                segment.fontVariant = styleMap['font-style'];
              }
              if (styleMap['font-size']) {
                // '24px' -> 24
                segment.fontSize = parseInt(styleMap['font-size'], 10);
              }
              if (styleMap.opacity) {
                segment.opacity = parseFloat(styleMap.opacity);
              }
            }

            segments.push(segment);
          }
        });
      }
    });
  });

  if (segments.length === 0) {
    return [{ text: '' }];
  }

  return segments;
};

/**
 * segments 배열을 plain text로 변환
 */
const segmentsToText = (segments: TextSegment[]): string => {
  return segments.map((s) => s.text).join('');
};

// ============================================
// 커스텀 TextNode (스타일 DOM 적용)
// ============================================

class ExtendedTextNode extends TextNode {
  static getType(): string {
    return 'extended-text';
  }

  static clone(node: ExtendedTextNode) {
    return new ExtendedTextNode(node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedTextNode): ExtendedTextNode {
    const node = $createTextNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node as ExtendedTextNode;
  }

  createDOM(config: any): HTMLElement {
    const dom = super.createDOM(config);
    const style = this.getStyle();
    if (style) {
      dom.setAttribute('style', style);
    }
    return dom;
  }

  updateDOM(prevNode: TextNode, dom: HTMLElement, config: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updated = super.updateDOM(prevNode as any, dom, config);
    const style = this.getStyle();
    if (style) {
      dom.setAttribute('style', style);
    } else {
      dom.removeAttribute('style');
    }
    return updated;
  }
}

// ============================================
// Lexical 플러그인들
// ============================================

/**
 * 초기화 플러그인 - item 데이터로 에디터 초기화
 */
const InitializePlugin: React.FC<{ item: TextItem }> = ({ item }) => {
  const [editor] = useLexicalComposerContext();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    editor.update(() => {
      const segments = item.segments || [{ text: item.text }];
      segmentsToEditorState(segments, editor, item);
    });
  }, [editor, item]);

  return null;
};

/**
 * 자동 포커스 플러그인 (편집 모드에서만)
 */
const AutoFocusPlugin: React.FC<{ editable: boolean }> = ({ editable }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (editable) {
      editor.focus();
    }
  }, [editor, editable]);

  return null;
};

/**
 * 선택 영역 스타일 추적 플러그인
 */
const SELECTION_MIXED = '__SELECTION_MIXED__';

const SelectionStylePlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const { setSelectionStyle } = useTextSelection();

  useEffect(() => {
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          setSelectionStyle({ hasSelection: false });
          return;
        }

        const isCollapsed = selection.isCollapsed();
        if (isCollapsed) {
          setSelectionStyle({ hasSelection: false });
          return;
        }

        // 선택된 노드들의 스타일 수집
        const nodes = selection.getNodes();
        const colors: (string | undefined)[] = [];
        const strokeColors: (string | undefined)[] = [];
        const fontFamilies: (string | undefined)[] = [];
        const fontWeights: (string | undefined)[] = [];
        const fontVariants: (string | undefined)[] = [];
        const fontSizes: (string | undefined)[] = [];
        const opacities: (string | undefined)[] = [];

        for (const node of nodes) {
          if ($isTextNode(node)) {
            const style = node.getStyle();
            const styleMap = style ? parseStyleString(style) : {};
            colors.push(styleMap.color);
            strokeColors.push(styleMap['-webkit-text-stroke-color']);
            fontFamilies.push(styleMap['font-family']);
            fontWeights.push(styleMap['font-weight']);
            fontVariants.push(styleMap['font-style']);
            fontSizes.push(styleMap['font-size']);
            opacities.push(styleMap.opacity);
          }
        }

        // 모든 값이 같은지 확인하는 헬퍼
        const getUnifiedValue = (
          values: (string | undefined)[],
        ): string | undefined => {
          if (values.length === 0) return undefined;
          const firstValue = values[0];
          const allSame = values.every((v) => v === firstValue);
          if (allSame) return firstValue;
          return SELECTION_MIXED;
        };

        setSelectionStyle({
          hasSelection: true,
          color: getUnifiedValue(colors),
          strokeColor: getUnifiedValue(strokeColors),
          fontFamily: getUnifiedValue(fontFamilies),
          fontWeight: getUnifiedValue(fontWeights),
          fontVariant: getUnifiedValue(fontVariants),
          fontSize: getUnifiedValue(fontSizes),
          opacity: getUnifiedValue(opacities),
        });
      });
    });

    return () => {
      unregister();
      // cleanup 시 selection 상태 초기화
      setSelectionStyle({ hasSelection: false });
    };
  }, [editor, setSelectionStyle]);

  return null;
};

/**
 * 스타일 적용 플러그인
 */
const StyleCommandsPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const {
    registerColorApplier,
    registerStrokeColorApplier,
    registerFontFamilyApplier,
    registerFontStyleApplier,
    registerFontSizeApplier,
    registerOpacityApplier,
    registerFontFamilyPreviewer,
    registerFontFamilyPreviewCanceler,
    registerFontStylePreviewer,
    registerFontStylePreviewCanceler,
  } = useTextSelection();

  // Selection 저장 ref
  const savedSelectionRef = useRef<{
    anchorKey: string;
    anchorOffset: number;
    focusKey: string;
    focusOffset: number;
  } | null>(null);

  // Preview 전 원본 EditorState 저장
  const originalEditorStateRef = useRef<EditorState | null>(null);
  // Preview 중인지 여부
  const isPreviewingRef = useRef(false);

  // Selection 저장
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      // Preview 중에는 selection 업데이트 하지 않음
      if (isPreviewingRef.current) return;

      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          savedSelectionRef.current = {
            anchorKey: selection.anchor.key,
            anchorOffset: selection.anchor.offset,
            focusKey: selection.focus.key,
            focusOffset: selection.focus.offset,
          };
        }
      });
    });
  }, [editor]);

  // 저장된 selection 복원 헬퍼
  const restoreSelection = () => {
    let selection = $getSelection();

    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
      const saved = savedSelectionRef.current;
      if (saved) {
        const anchorNode = $getNodeByKey(saved.anchorKey);
        const focusNode = $getNodeByKey(saved.focusKey);

        if (anchorNode && focusNode) {
          const newSelection = $createRangeSelection();
          newSelection.anchor.set(saved.anchorKey, saved.anchorOffset, 'text');
          newSelection.focus.set(saved.focusKey, saved.focusOffset, 'text');
          $setSelection(newSelection);
          selection = newSelection;
        }
      }
    }

    return selection;
  };

  // 색상 적용
  useEffect(() => {
    const unregister = registerColorApplier((color: string) => {
      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, { color });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerColorApplier]);

  useEffect(() => {
    const unregister = registerStrokeColorApplier((color: string) => {
      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, {
            '-webkit-text-stroke-color': color,
          });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerStrokeColorApplier]);

  // 폰트 적용 (최종 적용)
  useEffect(() => {
    const unregister = registerFontFamilyApplier((fontFamily: string) => {
      // Preview 취소하고 최종 적용
      isPreviewingRef.current = false;
      originalEditorStateRef.current = null;

      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, { 'font-family': fontFamily });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerFontFamilyApplier]);

  // 폰트 스타일 (weight + variant) 적용
  useEffect(() => {
    const unregister = registerFontStyleApplier((fontStyle) => {
      // Preview 취소하고 최종 적용
      isPreviewingRef.current = false;
      originalEditorStateRef.current = null;

      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, {
            'font-weight': fontStyle.weight,
            'font-style': fontStyle.variant,
          });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerFontStyleApplier]);

  // 폰트 사이즈 적용
  useEffect(() => {
    const unregister = registerFontSizeApplier((fontSize: number) => {
      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, { 'font-size': `${fontSize}px` });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerFontSizeApplier]);

  // 투명도 적용
  useEffect(() => {
    const unregister = registerOpacityApplier((opacity: number) => {
      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, { opacity: String(opacity) });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerOpacityApplier]);

  // 폰트 Preview (선택 영역에 임시 적용)
  useEffect(() => {
    const unregister = registerFontFamilyPreviewer((fontFamily: string) => {
      // 첫 preview 시 원본 상태 저장
      if (!isPreviewingRef.current) {
        originalEditorStateRef.current = editor.getEditorState();
        isPreviewingRef.current = true;
      }

      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, { 'font-family': fontFamily });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerFontFamilyPreviewer]);

  // 폰트 Preview 취소 (원본으로 복원)
  useEffect(() => {
    const unregister = registerFontFamilyPreviewCanceler(() => {
      if (originalEditorStateRef.current && isPreviewingRef.current) {
        editor.setEditorState(originalEditorStateRef.current);
        originalEditorStateRef.current = null;
        isPreviewingRef.current = false;
      }
    });
    return unregister;
  }, [editor, registerFontFamilyPreviewCanceler]);

  // 폰트 스타일 Preview (선택 영역에 임시 적용)
  useEffect(() => {
    const unregister = registerFontStylePreviewer((fontStyle) => {
      // 첫 preview 시 원본 상태 저장
      if (!isPreviewingRef.current) {
        originalEditorStateRef.current = editor.getEditorState();
        isPreviewingRef.current = true;
      }

      editor.update(
        () => {
          const selection = restoreSelection();
          if (!$isRangeSelection(selection)) return;
          $patchStyleText(selection, {
            'font-weight': fontStyle.weight,
            'font-style': fontStyle.variant,
          });
        },
        { discrete: true },
      );
    });
    return unregister;
  }, [editor, registerFontStylePreviewer]);

  // 폰트 스타일 Preview 취소 (원본으로 복원)
  useEffect(() => {
    const unregister = registerFontStylePreviewCanceler(() => {
      if (originalEditorStateRef.current && isPreviewingRef.current) {
        editor.setEditorState(originalEditorStateRef.current);
        originalEditorStateRef.current = null;
        isPreviewingRef.current = false;
      }
    });
    return unregister;
  }, [editor, registerFontStylePreviewCanceler]);

  return null;
};

// ============================================
// 메인 컴포넌트
// ============================================

export const RichTextEditor: React.FC<{
  item: TextItem;
  zIndex?: number;
  editable?: boolean;
  onComplete?: () => void;
}> = ({
  item: itemWithoutHoverPreview,
  zIndex,
  editable = true,
  onComplete,
}) => {
  const hoverPreviewContext = useContext(TextItemHoverPreviewContext);
  const item = useMemo(
    () =>
      overrideTextItemWithHoverPreview({
        textItem: itemWithoutHoverPreview,
        hoverPreview: hoverPreviewContext,
      }),
    [itemWithoutHoverPreview, hoverPreviewContext],
  );

  const { setState } = useWriteContext();
  const isLinkingEnabled = useLinkingEnabled();
  const { setSelectionStyle } = useTextSelection();

  // 컴포넌트 언마운트 시 선택 상태 초기화
  useEffect(() => {
    return () => {
      setSelectionStyle({ hasSelection: false });
    };
  }, [setSelectionStyle]);

  // 폰트 로드
  loadFontFromTextItem({
    fontFamily: item.fontFamily,
    fontVariant: item.fontStyle.variant,
    fontWeight: item.fontStyle.weight,
    fontInfosDuringRendering: null,
  });

  // segments 내 개별 폰트도 로드 (Rich Text 부분 스타일 지원)
  if (itemWithoutHoverPreview.segments) {
    for (const segment of itemWithoutHoverPreview.segments) {
      if (segment.fontFamily) {
        loadFontFromTextItem({
          fontFamily: segment.fontFamily,
          fontVariant: segment.fontVariant ?? 'normal',
          fontWeight: segment.fontWeight ?? '400',
          fontInfosDuringRendering: null,
        });
      }
    }
  }

  // 저장 핸들러
  const handleSave = useCallback(
    (editorState: EditorState) => {
      const segments = editorStateToSegments(editorState, item);
      const plainText = segmentsToText(segments);

      if (plainText.trim() === '') {
        setState({
          update: (state) => {
            return unmarkTextAsEditing(
              deleteItems(state, [item.id], { isLinkingEnabled }),
            );
          },
          commitToUndoStack: true,
        });
        return;
      }

      // 스타일이 적용된 segments만 저장
      const hasStyledSegments = segments.some(
        (s) =>
          s.color ||
          s.strokeColor ||
          s.fontFamily ||
          s.fontWeight ||
          s.fontVariant ||
          s.fontSize ||
          s.opacity !== undefined,
      );

      setState({
        update: (state) => {
          const newState = changeItem(state, item.id, (i) => {
            if (!isTextItem(i)) return i;
            return {
              ...i,
              text: plainText,
              segments: hasStyledSegments ? segments : undefined,
              ...syncEditedTitleToStore(
                i,
                plainText,
                hasStyledSegments ? segments : undefined,
              ),
            };
          });
          return unmarkTextAsEditing(newState);
        },
        commitToUndoStack: true,
      });
    },
    [setState, item.id, isLinkingEnabled],
  );

  // 변경 핸들러
  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (!editable || !item.resizeOnEdit) return;

      const segments = editorStateToSegments(editorState, item);
      const plainText = segmentsToText(segments);
      const hasStyledSegments = segments.some(
        (s) =>
          s.color ||
          s.strokeColor ||
          s.fontFamily ||
          s.fontWeight ||
          s.fontVariant ||
          s.fontSize ||
          s.opacity !== undefined,
      );

      setState({
        update: (state) =>
          changeItem(state, item.id, (i) => {
            if (!isTextItem(i)) return i;
            return {
              ...i,
              text: plainText,
              segments: hasStyledSegments ? segments : undefined,
              ...syncEditedTitleToStore(
                i,
                plainText,
                hasStyledSegments ? segments : undefined,
              ),
            };
          }),
        commitToUndoStack: false,
      });
    },
    [setState, item, editable],
  );

  // Blur 핸들러
  const handleBlur = useCallback(
    (e: React.FocusEvent, editor: LexicalEditor) => {
      if (!editable) return;

      // 편집 종료 시 selection 상태 초기화
      setSelectionStyle({ hasSelection: false });

      const editorState = editor.getEditorState();
      handleSave(editorState);
      onComplete?.();
    },
    [handleSave, onComplete, editable, setSelectionStyle],
  );

  // Lexical 설정
  const initialConfig = useMemo(
    () => ({
      namespace: `text-editor-${item.id}`,
      onError: (error: Error) => console.error('Lexical error:', error),
      editable,
      nodes: [
        ExtendedTextNode,
        {
          replace: TextNode,
          with: (node: TextNode) => new ExtendedTextNode(node.__text),
        },
      ],
    }),
    [item.id, editable],
  );

  // 스타일 계산
  const containerStyle = useMemo(
    (): React.CSSProperties => ({
      position: 'absolute',
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
      zIndex: zIndex ?? 1,
      transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
    }),
    [item.left, item.top, item.width, item.height, item.rotation, zIndex],
  );

  const contentEditableStyle = useMemo((): React.CSSProperties => {
    const cssFontFamily = resolveFontFamilyCssStack(item.fontFamily);

    return {
      outline: 'none',
      width: '100%',
      height: '100%',
      fontSize: item.fontSize,
      color: item.color,
      lineHeight: String(item.lineHeight),
      letterSpacing: `${item.letterSpacing}px`,
      whiteSpace: item.whiteSpace ?? 'pre-wrap',
      display: 'block',
      fontFamily: cssFontFamily,
      ...turnFontStyleIntoCss(item.fontStyle),
      overflow: item.overflow ?? 'visible',
      textOverflow: item.textOverflow,
      ...WORD_BREAK_STYLE,
      boxSizing: 'border-box',
      userSelect: editable ? 'text' : 'none',
      textAlign: item.align,
      caretColor: editable ? 'auto' : 'transparent',
      direction: item.direction,
      opacity: item.opacity,
      pointerEvents: 'auto',
      WebkitTextStroke: item.strokeWidth
        ? `${item.strokeWidth}px ${item.strokeColor}`
        : undefined,
      paintOrder: item.strokeWidth ? 'stroke' : undefined,
      filter: getShadowFilter(item),
      cursor: editable ? 'text' : 'default',
    };
  }, [item, editable]);

  // 편집 모드일 때 포인터/마우스 이벤트가 캔버스로 전파되지 않도록 함
  // (캔버스의 marquee selection 방지)
  const stopPropagationIfEditable = useCallback(
    (e: React.SyntheticEvent) => {
      if (editable) {
        e.stopPropagation();
      }
    },
    [editable],
  );

  if (!editable) {
    return <div style={containerStyle}><StaticRichText text={item.text} segments={item.segments} style={contentEditableStyle}/></div>;
  }

  return (
    <div
      style={containerStyle}
      onPointerDown={stopPropagationIfEditable}
      onPointerMove={stopPropagationIfEditable}
      onPointerUp={stopPropagationIfEditable}
      onMouseDown={stopPropagationIfEditable}
      onMouseMove={stopPropagationIfEditable}
      onMouseUp={stopPropagationIfEditable}
      onDragStart={stopPropagationIfEditable}
    >
      <style>{`
        .rich-text-content-editable:focus {
          outline: none;
        }
        .rich-text-content-editable p {
          margin: 0;
          padding: 0;
          text-align: inherit !important;
        }
        .rich-text-content-editable * {
          font-family: inherit;
          font-weight: inherit;
          font-style: inherit;
        }
        .rich-text-content-editable *:not([style]) {
          color: inherit;
        }
      `}</style>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={contentEditableStyle}
              className="rich-text-content-editable"
              spellCheck={false}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        {editable && <HistoryPlugin />}
        {editable && (
          <OnChangePlugin
            onChange={handleChange}
            ignoreSelectionChange
          />
        )}
        <InitializePlugin item={item} />
        {editable && <AutoFocusPlugin editable={editable} />}
        {editable && <SelectionStylePlugin />}
        {editable && <StyleCommandsPlugin />}
        {editable && <BlurHandlerPlugin onBlur={handleBlur} />}
      </LexicalComposer>
    </div>
  );
};

/**
 * Blur 이벤트 처리 플러그인
 */
const BlurHandlerPlugin: React.FC<{
  onBlur: (e: React.FocusEvent, editor: LexicalEditor) => void;
}> = ({ onBlur }) => {
  const [editor] = useLexicalComposerContext();
  const lastPointerDownTargetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      lastPointerDownTargetRef.current = e.target as HTMLElement | null;
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleBlur = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const lastPointerDownTarget = lastPointerDownTargetRef.current;
      lastPointerDownTargetRef.current = null;
      const targetToCheck =
        relatedTarget ||
        (document.activeElement as HTMLElement | null) ||
        lastPointerDownTarget;

      if (targetToCheck) {
        const isInspectorArea =
          targetToCheck.closest('[data-text-inspector]') ||
          targetToCheck.closest('[data-radix-popper-content-wrapper]') ||
          targetToCheck.closest('[data-radix-popover-content]') ||
          targetToCheck.closest('[role="dialog"]');

        if (isInspectorArea) {
          return;
        }
      }

      onBlur(e as unknown as React.FocusEvent, editor);
    };

    rootElement.addEventListener('blur', handleBlur);
    return () => {
      rootElement.removeEventListener('blur', handleBlur);
    };
  }, [editor, onBlur]);

  return null;
};
